import React, { useState, useEffect, useCallback } from 'react'
import { Heading, Text, Button, Loader, Toast, Flex } from '@vibe/core'
import { Add } from '@vibe/icons'
import { Layout } from '../components/layout'
import { AuthGuard } from '../components/common/AuthGuard'
import { PermissionGuard } from '../components/common/PermissionGuard'
import { UserTable } from '../components/dashboard'
import { UserForm } from '../components/forms'
import { apiService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import type {
  UserInfo,
  GetUsersResponse,
  CreateUserRequest,
  UpdateUserRequest,
  PaginationParams,
} from '../types'
import { getErrorMessage } from '../utils/errorHandling'

/**
 * User Management page for administrators to manage users and their roles
 */
const UserManagement: React.FC = () => {
  const { user } = useAuth()
  const permissions = usePermissions()

  // State management
  const [users, setUsers] = useState<UserInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showUserForm, setShowUserForm] = useState(false)
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null)

  // Pagination state
  const [pageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [nextKey, setNextKey] = useState<string | undefined>()

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [toastType, setToastType] = useState<'positive' | 'negative'>(
    'positive'
  )

  /**
   * Check if user has permission to manage users
   */
  const canManageUsers = permissions?.canManageUsers() || false

  /**
   * Load users data from API
   */
  const loadUsers = useCallback(
    async (resetPagination = false, customNextKey?: string) => {
      try {
        setLoading(resetPagination)
        setError(null)

        const params: PaginationParams = {
          size: pageSize,
          ...(resetPagination ? {} : { start: customNextKey }),
        }

        const response: GetUsersResponse = await apiService.getUsers(params)

        // Extract data from the response structure
        const { data } = response
        const items = data.items || []
        const totalItems = data.total_items || 0
        const responseNextKey = data.next_key

        if (resetPagination) {
          setUsers(items)
        } else {
          setUsers(prev => [...prev, ...items])
        }

        setTotalItems(totalItems)
        setNextKey(responseNextKey)
      } catch (err) {
        const errorMessage = getErrorMessage(err) || 'Error al cargar usuarios'
        setError(errorMessage)
        showToast(errorMessage, 'negative')
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [pageSize]
  )

  /**
   * Handle user creation
   */
  const handleCreateUser = async (userData: CreateUserRequest) => {
    try {
      const newUser = await apiService.createUser(userData)

      // Use the user data returned from the API directly
      const completeUser: UserInfo = {
        ...newUser,
        // Ensure required fields are present
        id: newUser.id || userData.email,
        email: newUser.email || userData.email,
        name: newUser.name || userData.name,
        role: newUser.role || userData.role,
        status: newUser.status || 'active',
        created_at: newUser.created_at || new Date().toISOString(),
        last_login: newUser.last_login || undefined,
        created_by: newUser.created_by || 'system',
        cognito_username:
          newUser.cognito_username || newUser.id || userData.email,
        cognito_groups: newUser.cognito_groups || [
          newUser.role || userData.role,
        ],
      }

      setUsers(prev => [completeUser, ...prev])
      setTotalItems(prev => prev + 1)
      setShowUserForm(false)
      showToast('Usuario creado exitosamente', 'positive')
    } catch (err) {
      const errorMessage = getErrorMessage(err) || 'Error al crear usuario'
      showToast(errorMessage, 'negative')
      throw err // Re-throw to let form handle the error
    }
  }

  /**
   * Handle user update
   */
  const handleUpdateUser = async (
    userId: string,
    userData: UpdateUserRequest
  ) => {
    try {
      const updatedUser = await apiService.updateUser(userId, userData)
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? {
                ...user, // Preserve existing fields like created_at
                ...updatedUser, // Override with updated fields
                id: user.id, // Ensure ID is preserved
              }
            : user
        )
      )
      setEditingUser(null)
      setShowUserForm(false)
      showToast('Usuario actualizado exitosamente', 'positive')
    } catch (err) {
      const errorMessage = getErrorMessage(err) || 'Error al actualizar usuario'
      showToast(errorMessage, 'negative')
      throw err // Re-throw to let form handle the error
    }
  }

  /**
   * Handle user deletion
   */
  const handleDeleteUser = async (userId: string) => {
    try {
      await apiService.deleteUser(userId)
      setUsers(prev => prev.filter(user => user.id !== userId))
      setTotalItems(prev => prev - 1)
      showToast('Usuario eliminado exitosamente', 'positive')
    } catch (err) {
      const errorMessage = getErrorMessage(err) || 'Error al eliminar usuario'
      showToast(errorMessage, 'negative')
      throw err // Re-throw so UserTable can handle the error state
    }
  }

  /**
   * Load more users (pagination)
   */
  const handleLoadMore = useCallback(async () => {
    if (!nextKey || loading || loadingMore) {
      return
    }

    try {
      setLoadingMore(true)
      await loadUsers(false, nextKey)
    } catch (err) {
      const errorMessage =
        getErrorMessage(err) || 'Error al cargar más usuarios'
      showToast(errorMessage, 'negative')
    }
  }, [nextKey, loading, loadingMore, loadUsers])

  /**
   * Show toast notification
   */
  const showToast = (message: string, type: 'positive' | 'negative') => {
    setToastMessage(message)
    setToastType(type)
  }

  /**
   * Close toast notification
   */
  const closeToast = () => {
    setToastMessage(null)
  }

  /**
   * Handle edit user
   */
  const handleEditUser = (user: UserInfo) => {
    setEditingUser(user)
    setShowUserForm(true)
  }

  /**
   * Handle close form
   */
  const handleCloseForm = () => {
    setShowUserForm(false)
    setEditingUser(null)
  }

  /**
   * Initial data loading
   */
  useEffect(() => {
    loadUsers(true)
  }, [loadUsers])

  return (
    <AuthGuard>
      <PermissionGuard requirePermission={() => canManageUsers}>
        <Layout>
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Flex justify="space-between" align="center">
                <div>
                  <Heading type="h1" className="mb-2">
                    Gestión de Usuarios
                  </Heading>
                  <Text type="text3" color="secondary" className="mt-1">
                    Administra los usuarios del sistema
                  </Text>
                </div>
                <Button
                  onClick={() => setShowUserForm(true)}
                  kind="primary"
                  leftIcon={Add}
                  disabled={!canManageUsers}
                >
                  Crear Usuario
                </Button>
              </Flex>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg shadow-sm">
              {!loading && users.length > 0 && (
                <UserTable
                  users={users}
                  loading={loadingMore}
                  onLoadMore={nextKey ? handleLoadMore : undefined}
                  onEditUser={handleEditUser}
                  onDeleteUser={handleDeleteUser}
                  totalItems={totalItems}
                  hasMore={!!nextKey}
                  currentUserId={user?.id || ''}
                  canManageUsers={canManageUsers}
                />
              )}

              {loading && users.length === 0 && (
                <div className="p-8 text-center">
                  <Loader size="large" />
                  <Text className="mt-4">Cargando usuarios...</Text>
                </div>
              )}

              {error && !loading && (
                <div className="p-8 text-center">
                  <Text color="negative" className="mb-4">
                    {error}
                  </Text>
                  <Button onClick={() => loadUsers(true)}>Reintentar</Button>
                </div>
              )}

              {!loading && !error && users.length === 0 && (
                <div className="p-8 text-center">
                  <Text className="mb-4">No se encontraron usuarios.</Text>
                  <Button onClick={() => setShowUserForm(true)}>
                    Crear primer usuario
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* User Form Modal */}
          {showUserForm && (
            <UserForm
              user={editingUser}
              onCreateUser={editingUser ? undefined : handleCreateUser}
              onUpdateUser={editingUser ? handleUpdateUser : undefined}
              onClose={handleCloseForm}
              isOpen={showUserForm}
            />
          )}

          {/* Toast Notification */}
          {toastMessage && (
            <Toast
              open={!!toastMessage}
              type={toastType}
              onClose={closeToast}
              autoHideDuration={5000}
              className="fixed top-4 right-4 z-50"
            >
              {toastMessage}
            </Toast>
          )}
        </Layout>
      </PermissionGuard>
    </AuthGuard>
  )
}

export default UserManagement
